import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getMyAssignments, type MyAssignment } from '../api/assignment';
import useThrowAsyncError from '../hooks/useThrowAsyncError';
import IdentityContext from './IdentityContext';
import FeatureTogglesContext from './FeatureTogglesContext';
import { SCORE_STATUS } from '../constants/ScoreStatus';
import { Module } from '../api/module';

type MyAssignmentsContextValue = {
  assignments: MyAssignment[];
  refreshAssignments: () => Promise<void>;
  isLoading: boolean;
  getIncompleteAssignmentCountByModule: (module?: Module) => number;
};

const defaultMyAssignmentsContextValue: MyAssignmentsContextValue = {
  assignments: [],
  refreshAssignments: () => Promise.resolve(),
  isLoading: false,
  getIncompleteAssignmentCountByModule: () => 0,
};

const MyAssignmentsContext = createContext<MyAssignmentsContextValue>(defaultMyAssignmentsContextValue);

export default MyAssignmentsContext;

export const MyAssignmentsContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [assignments, setAssignments] = useState<MyAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const {
    identity: { isAuthenticated },
  } = useContext(IdentityContext);

  const { IANUA } = useContext(FeatureTogglesContext);

  const handleError = useThrowAsyncError();

  const fetchAssignments = useCallback(async () => {
    if (isAuthenticated) {
      setIsLoading(true);
      try {
        const fetchedAssignments = await getMyAssignments(IANUA);
        setAssignments(fetchedAssignments);
      } catch (err) {
        handleError(err);
      } finally {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated, handleError, IANUA]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const incompleteAssignmentsByModule = useMemo(() => {
    const map: Record<string, number> = {};
    for (const assignment of assignments) {
      const status = assignment.score?.status;
      if (status === SCORE_STATUS.NOT_STARTED || status === SCORE_STATUS.BUSY) {
        map[assignment.moduleId] = (map[assignment.moduleId] || 0) + 1;
      }
    }
    return map;
  }, [assignments]);

  const getIncompleteAssignmentCountByModule = useCallback(
    (module?: Module) => {
      if (!module) return 0;
      const subModulesCount = module.subModules.length
        ? module.subModules.reduce(
            (acc: number, subModule: string) => acc + (incompleteAssignmentsByModule[subModule] || 0),
            0,
          )
        : 0;
      return (incompleteAssignmentsByModule[module.id] || 0) + subModulesCount;
    },
    [incompleteAssignmentsByModule],
  );

  const myAssignmentsContextValue = useMemo(
    () => ({
      assignments,
      refreshAssignments: fetchAssignments,
      isLoading,
      getIncompleteAssignmentCountByModule,
    }),
    [assignments, fetchAssignments, isLoading, getIncompleteAssignmentCountByModule],
  );

  return <MyAssignmentsContext.Provider value={myAssignmentsContextValue}>{children}</MyAssignmentsContext.Provider>;
};
